import { FormEvent, useCallback, useEffect, useState } from "react";
import { Check, LoaderCircle, Pencil, Plus, SlidersHorizontal, X } from "lucide-react";

import {
  ApiError,
  createAddon,
  createService,
  getCatalog,
  updateAddon,
  updateService,
} from "./api";
import type {
  CatalogItemInput,
  ServiceAddon,
  ServiceAudience,
  ServiceType,
  ServiceTypeInput,
} from "./types";

export function CatalogPage({ canWrite }: { canWrite: boolean }) {
  const [services, setServices] = useState<ServiceType[]>([]);
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [composer, setComposer] = useState<"service" | "addon">();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const catalog = await getCatalog();
      setServices(catalog.services);
      setAddons(catalog.addons);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  return (
    <div className="page-content catalog-page">
      <header className="page-intro client-intro">
        <div>
          <span className="eyebrow lime">Configurable catalog</span>
          <h1>Shape the services TLC offers.</h1>
          <p>
            Keep customer-facing choices accurate without changing code. Disabled items remain in
            history but disappear from the public catalog.
          </p>
        </div>
        {canWrite ? (
          <div className="catalog-actions">
            <button
              className="secondary-button compact-action"
              onClick={() => setComposer("addon")}
            >
              <Plus /> Add-on
            </button>
            <button
              className="primary-button compact-action"
              onClick={() => setComposer("service")}
            >
              <Plus /> Service
            </button>
          </div>
        ) : null}
      </header>

      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t refresh the catalog.<button onClick={() => void load()}>Try again</button>
        </div>
      ) : null}

      {composer ? (
        <CatalogComposer
          kind={composer}
          onCancel={() => setComposer(undefined)}
          onSaved={async () => {
            setComposer(undefined);
            await load();
          }}
        />
      ) : null}

      {loading && services.length === 0 && addons.length === 0 ? (
        <div className="table-state">
          <LoaderCircle className="spin" /> Loading catalog…
        </div>
      ) : (
        <div className="catalog-grid">
          <CatalogSection
            title="Service types"
            eyebrow="Core work"
            empty="No service types are configured yet."
            items={services}
            canWrite={canWrite}
            onSaved={load}
          />
          <CatalogSection
            title="Add-ons"
            eyebrow="Extra care"
            empty="No add-ons are configured yet."
            items={addons}
            canWrite={canWrite}
            onSaved={load}
          />
        </div>
      )}
    </div>
  );
}

function CatalogSection<T extends ServiceType | ServiceAddon>({
  title,
  eyebrow,
  empty,
  items,
  canWrite,
  onSaved,
}: {
  title: string;
  eyebrow: string;
  empty: string;
  items: T[];
  canWrite: boolean;
  onSaved: () => Promise<void>;
}) {
  return (
    <section className="content-card catalog-column">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
        <span className="catalog-count">{items.length}</span>
      </div>
      <div className="catalog-list">
        {items.length === 0 ? <p className="catalog-empty">{empty}</p> : null}
        {items.map((item) => (
          <CatalogCard key={item.id} item={item} canWrite={canWrite} onSaved={onSaved} />
        ))}
      </div>
    </section>
  );
}

function CatalogCard({
  item,
  canWrite,
  onSaved,
}: {
  item: ServiceType | ServiceAddon;
  canWrite: boolean;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const isService = "audience" in item;
  if (editing) {
    return (
      <CatalogForm
        kind={isService ? "service" : "addon"}
        item={item}
        onCancel={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          await onSaved();
        }}
      />
    );
  }
  return (
    <article className={`catalog-card ${item.active ? "" : "inactive"}`}>
      <div className="catalog-card-top">
        <div>
          <span className={`availability ${item.active ? "active" : ""}`}>
            {item.active ? <Check /> : null}
            {item.active ? "Active" : "Hidden"}
          </span>
          <h3>{item.name}</h3>
        </div>
        {canWrite ? (
          <button
            className="icon-button"
            aria-label={`Edit ${item.name}`}
            onClick={() => setEditing(true)}
          >
            <Pencil />
          </button>
        ) : null}
      </div>
      <p>{item.description || "No description added yet."}</p>
      <footer>
        <span>{item.slug}</span>
        <span>{isService ? humanAudience(item.audience) : `Order ${item.sortOrder}`}</span>
      </footer>
    </article>
  );
}

function CatalogComposer({
  kind,
  onCancel,
  onSaved,
}: {
  kind: "service" | "addon";
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  return (
    <section className="content-card catalog-composer">
      <div className="section-heading">
        <div>
          <span className="eyebrow">New catalog item</span>
          <h2>Add {kind === "service" ? "a service type" : "an add-on"}</h2>
        </div>
        <button className="icon-button" aria-label="Close form" onClick={onCancel}>
          <X />
        </button>
      </div>
      <CatalogForm kind={kind} onCancel={onCancel} onSaved={onSaved} />
    </section>
  );
}

function CatalogForm({
  kind,
  item,
  onCancel,
  onSaved,
}: {
  kind: "service" | "addon";
  item?: ServiceType | ServiceAddon;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setError(undefined);
    const fields = new FormData(event.currentTarget);
    const base: CatalogItemInput = {
      name: String(fields.get("name")).trim(),
      slug: String(fields.get("slug")).trim().toLowerCase(),
      description: nullable(fields.get("description")),
      active: fields.get("active") === "on",
      sortOrder: Number(fields.get("sortOrder")),
    };
    try {
      if (kind === "service") {
        const input: ServiceTypeInput = {
          ...base,
          audience: String(fields.get("audience")) as ServiceAudience,
        };
        await (item ? updateService(item.id, input) : createService(input));
      } else {
        await (item ? updateAddon(item.id, base) : createAddon(base));
      }
      await onSaved();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === "SLUG_CONFLICT"
          ? "That URL key is already in use. Choose another slug."
          : "We couldn’t save this item. Review the fields and try again.",
      );
      setSending(false);
    }
  }

  return (
    <form className="catalog-form" onSubmit={submit}>
      <label>
        Name
        <input name="name" defaultValue={item?.name} maxLength={120} required />
      </label>
      <label>
        URL key
        <input
          name="slug"
          defaultValue={item?.slug}
          placeholder="deep-cleaning"
          pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
          required
        />
      </label>
      {kind === "service" ? (
        <label>
          Audience
          <select
            name="audience"
            defaultValue={item && "audience" in item ? item.audience : "residential"}
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="both">Residential & commercial</option>
          </select>
        </label>
      ) : null}
      <label>
        Display order
        <input
          name="sortOrder"
          type="number"
          min="0"
          max="10000"
          defaultValue={item?.sortOrder ?? 0}
          required
        />
      </label>
      <label className="catalog-description">
        Description
        <textarea
          name="description"
          defaultValue={item?.description ?? ""}
          rows={3}
          maxLength={4000}
        />
      </label>
      <label className="toggle-field">
        <input name="active" type="checkbox" defaultChecked={item?.active ?? true} /> Available in
        the live catalog
      </label>
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending}>
          {sending ? <LoaderCircle className="spin" /> : <SlidersHorizontal />} Save item
        </button>
      </div>
    </form>
  );
}

function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}

function humanAudience(value: ServiceAudience) {
  return value === "both"
    ? "Residential & commercial"
    : `${value[0].toUpperCase()}${value.slice(1)}`;
}
